import { useEffect, useState } from 'react';
import { ApiError, syncApi, type SyncStatus } from '../api';
import { savePrefs, usePrefs } from '../prefs';

/** Kijkgeschiedenis bijhouden op Trakt en/of SIMKL, eenmalig overzetten, en optioneel terug naar Plex. */
export function SyncSettings({ refreshKey = 0 }: { refreshKey?: number }) {
  const p = usePrefs();
  const [st, setSt] = useState<SyncStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => syncApi.status().then(setSt).catch(() => setSt(null));
  useEffect(() => {
    void load();
  }, [refreshKey]);
  // Overzetten loopt op de achtergrond: even bijhouden
  useEffect(() => {
    if (st?.import?.state !== 'running') return;
    const t = window.setTimeout(() => void load(), 3000);
    return () => window.clearTimeout(t);
  }, [st]);

  if (!st || (!st.trakt.configured && !st.simkl.configured)) return null;
  const tl = st.trakt.configured && st.trakt.linked;
  const sl = st.simkl.configured && st.simkl.linked;
  const save = (patch: Parameters<typeof savePrefs>[0]) => void savePrefs(patch).catch(() => undefined);

  const run = async (what: 'import' | 'pull') => {
    setBusy(what);
    setMsg(null);
    try {
      if (what === 'import') await syncApi.import({ trakt: tl && p.traktScrobble, simkl: sl && p.simklSync });
      else await syncApi.pull();
      await load();
    } catch (err) {
      setMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'Dat lukte niet.');
    } finally {
      setBusy(null);
    }
  };

  const imp = st.import;
  return (
    <section className="settings-block" aria-label="Kijkgeschiedenis">
      <h2>Kijkgeschiedenis</h2>
      <p className="subtle hint">Houd bij wat je kijkt op je eigen Trakt- en/of SIMKL-account: tijdens het kijken, en als je iets als (niet) bekeken markeert. Werkt op het web, de iPhone en de Apple TV.</p>
      {!tl && !sl && <p className="subtle">Koppel hierboven eerst Trakt of SIMKL.</p>}
      {tl && (
        <label className="studio-toggle"><span>Bijwerken op Trakt{st.trakt.username ? ` (${st.trakt.username})` : ''}</span><span className="toggle"><input type="checkbox" checked={p.traktScrobble} onChange={(e) => save({ traktScrobble: e.target.checked })} /></span></label>
      )}
      {tl && (
        <label className="studio-toggle"><span>Verder kijken aanvullen met Trakt <span className="subtle">— wat je elders half keek en de volgende aflevering van je series</span></span><span className="toggle"><input type="checkbox" checked={p.traktContinue} onChange={(e) => save({ traktContinue: e.target.checked })} /></span></label>
      )}
      {sl && (
        <label className="studio-toggle"><span>Bijwerken op SIMKL{st.simkl.username ? ` (${st.simkl.username})` : ''}</span><span className="toggle"><input type="checkbox" checked={p.simklSync} onChange={(e) => save({ simklSync: e.target.checked })} /></span></label>
      )}
      {sl && st.simkl.canWrite === false && (
        <p className="sync-warn" role="alert">SIMKL is eerder gekoppeld met alleen leesrechten. Ontkoppel SIMKL hierboven en koppel het opnieuw, dan mag tumedia je geschiedenis bijwerken.</p>
      )}
      {(tl || sl) && (
        <>
          <label className="studio-toggle">
            <span>Ook terug naar Plex <span className="subtle">— wat je elders als bekeken markeert, wordt in Plex ook bekeken (elk half uur)</span></span>
            <span className="toggle"><input type="checkbox" checked={p.syncBack} onChange={(e) => save({ syncBack: e.target.checked })} /></span>
          </label>
          {p.syncBack && (
            <div className="form-actions">
              <button type="button" className="btn btn-sm" disabled={busy === 'pull'} onClick={() => void run('pull')}>{busy === 'pull' ? 'Bezig…' : 'Nu terughalen'}</button>
              {st.back?.lastRun && <span className="subtle">Laatst: {new Date(st.back.lastRun).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {st.back.lastResult}</span>}
            </div>
          )}
          <div className="sync-import">
            <b>Bestaande geschiedenis overzetten</b>
            <span className="subtle">Alles wat je in Plex al hebt gekeken in één keer naar {[tl && p.traktScrobble ? 'Trakt' : null, sl && p.simklSync ? 'SIMKL' : null].filter(Boolean).join(' en ') || 'Trakt/SIMKL (zet hierboven eerst bijwerken aan)'}.</span>
            <div className="form-actions">
              <button type="button" className="btn btn-sm" disabled={!!busy || imp?.state === 'running' || !((tl && p.traktScrobble) || (sl && p.simklSync))} onClick={() => void run('import')}>
                {imp?.state === 'running' ? 'Bezig met overzetten…' : 'Geschiedenis overzetten'}
              </button>
              {imp?.state === 'done' && <span className="srv-ok">✓ {imp.movies} films en {imp.episodes} afleveringen overgezet</span>}
              {imp?.state === 'error' && <span className="error">Mislukt: {imp.message}</span>}
            </div>
          </div>
        </>
      )}
      {!!st.pending && <p className="subtle hint">{st.pending} {st.pending === 1 ? 'wijziging wacht' : 'wijzigingen wachten'} nog: Trakt/SIMKL was even niet bereikbaar. tumedia probeert het elke paar minuten opnieuw.</p>}
      {msg && <p className="error" role="alert">{msg}</p>}
    </section>
  );
}
